import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  usePickingLists,
  useAssignPicker,
  useStartPicking,
  useCompletePicking,
} from '../hooks/useWmsPicking';
import { useDebounce } from '../hooks/useDebounce';
import { PickingListsFilters } from '../components/picking/PickingListsFilters';
import { PickingListsTable } from '../components/picking/PickingListsTable';
import { AssignPickerModal } from '../components/picking/AssignPickerModal';
import type {
  PickingList,
  PickingListStatus,
  PickingListsResponse,
  AssignPickerInput,
} from '../types';

export default function PickingListsPage() {
  const navigate = useNavigate();

  // Filter state
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [status, setStatus] = useState('all');
  const [pageNumber, setPageNumber] = useState(0);
  const pageLength = 50;

  // Reset to first page when debounced search changes
  useEffect(() => {
    setPageNumber(0);
  }, [debouncedSearch]);

  const handleStatusChange = (value: string) => {
    setStatus(value);
    setPageNumber(0);
  };

  // Modal state
  const [assignPickingList, setAssignPickingList] = useState<PickingList | null>(null);

  // Queries
  const pickingListsQuery = usePickingLists({
    search: debouncedSearch || undefined,
    status: status === 'all' ? undefined : (status as PickingListStatus),
    page_number: pageNumber,
    page_length: pageLength,
  });

  // Mutations
  const assignMutation = useAssignPicker();
  const startMutation = useStartPicking();
  const completeMutation = useCompletePicking();

  const pickingListsData = pickingListsQuery.data as PickingListsResponse | undefined;
  const isLoading = pickingListsQuery.isLoading;
  const error = pickingListsQuery.error;

  const pickingLists = pickingListsData?.items || [];
  const total = pickingListsData?.total || 0;

  const handleRowClick = (pl: PickingList) => {
    navigate(`/wms/picking/${pl.id}`);
  };

  const handleAssign = async (data: AssignPickerInput) => {
    if (!assignPickingList) return;
    await assignMutation.mutateAsync({
      pickingListId: assignPickingList.id,
      data,
    });
  };

  const handleStart = async (pl: PickingList) => {
    await startMutation.mutateAsync(pl.id);
  };

  const handleComplete = async (pl: PickingList) => {
    await completeMutation.mutateAsync(pl.id);
  };

  const totalPages = Math.ceil(total / pageLength);

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Picking Lists</h1>
          <p className="text-muted-foreground">
            Manage warehouse picking operations
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <PickingListsFilters
            search={search}
            onSearchChange={setSearch}
            status={status}
            onStatusChange={handleStatusChange}
          />
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="pt-6">
          {error ? (
            <div className="text-center py-8 text-destructive">
              Error loading picking lists: {error.message}
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <>
              <PickingListsTable
                pickingLists={pickingLists}
                onRowClick={handleRowClick}
                onAssign={setAssignPickingList}
                onStart={handleStart}
                onComplete={handleComplete}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {pageNumber * pageLength + 1} to{' '}
                    {Math.min((pageNumber + 1) * pageLength, total)} of {total} picking lists
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 text-sm border rounded hover:bg-accent disabled:opacity-50"
                      onClick={() => setPageNumber((p) => Math.max(0, p - 1))}
                      disabled={pageNumber === 0}
                    >
                      Previous
                    </button>
                    <button
                      className="px-3 py-1 text-sm border rounded hover:bg-accent disabled:opacity-50"
                      onClick={() => setPageNumber((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={pageNumber >= totalPages - 1}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Assign Picker Modal */}
      <AssignPickerModal
        open={!!assignPickingList}
        onClose={() => setAssignPickingList(null)}
        onSubmit={handleAssign}
        pickingList={assignPickingList}
        isLoading={assignMutation.isPending}
      />
    </div>
  );
}
